import {useEffect, useMemo, type ReactNode} from 'react';
import {RoomContext} from '@livekit/components-react';
import {isActivePhase} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import {useGroupCallState, useRtcFeatures} from '../../groupCalls/hooks/useGroupCall';
import {ensureGroupCallServiceStarted, groupCallService} from '../../groupCalls/services/groupCallService';
import {CallMemberDirectoryContext, ParticipantViewsContext, createRoomMemberDirectory} from '../../groupCalls/components/callContext';
import {CallAudio} from '../../groupCalls/components/CallAudio';
import {watchMicrophoneProcessing} from '../../groupCalls/platform/microphoneDiagnostics';
import '../../groupCalls/groupCalls.css';

const GroupEngine = ({children}: {children: ReactNode}) => {
    const features = useRtcFeatures();
    const state = useGroupCallState();

    useEffect(() => {
        ensureGroupCallServiceStarted();
    }, []);

    useEffect(() => {
        groupCallService.setFeatures(features);
    }, [features]);

    const matrixRoom = state.roomId ? matrixService.getRoom(state.roomId) : null;
    const directory = useMemo(() => createRoomMemberDirectory(matrixRoom), [matrixRoom]);
    const views = useMemo(() => new Map(state.participants.map(view => [view.identity, view])), [state.participants]);
    const livekitRoom = isActivePhase(state.phase) ? groupCallService.controller?.room : undefined;

    useEffect(() => (livekitRoom ? watchMicrophoneProcessing(livekitRoom) : undefined), [livekitRoom]);

    return (
        <CallMemberDirectoryContext.Provider value={directory}>
            <ParticipantViewsContext.Provider value={views}>
                <RoomContext.Provider value={livekitRoom}>
                    {livekitRoom && <CallAudio/>}
                    {children}
                </RoomContext.Provider>
            </ParticipantViewsContext.Provider>
        </CallMemberDirectoryContext.Provider>
    );
};

export const GroupCallHost = ({enabled, children}: {enabled: boolean; children: ReactNode}) =>
    enabled ? <GroupEngine>{children}</GroupEngine> : <>{children}</>;
